import { FC, useMemo } from 'react';
import {
  Icon,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import { RangeColor } from '@/types';

import '@/components/GradientBar/GradientBar.scss';

interface GradientBarProps {
  data: RangeColor[];
  value: number;
  label?: string;
  description?: string;
}

export const GradientBar: FC<GradientBarProps> = ({
  data,
  value,
  label,
  description,
}) => {
  const sorted = data;

  const gradient = useMemo(() => {
    const step = 100 / (sorted.length - 1);
    const stops = sorted.map((d, i) => `${d.color} ${i * step}%`).join(', ');
    return `linear-gradient(to bottom, ${stops})`;
  }, [sorted]);

  const markerIndex = sorted.findIndex(
    (d) => value >= d.range[0] && value < d.range[1],
  );

  const markerPosition =
    markerIndex >= 0 ? ((markerIndex + 0.5) / sorted.length) * 100 : 0;

  return (
    <div className="gradient-bar">
      {label && (
        <div className="gradient-bar__label">
          <Typography tag="textdefault_strong">{label}</Typography>
          {description && (
            <Tooltip title={description} arrow>
              <Icon
                icon="IconInformation"
                color="var(--color-default)"
                iconSize={0.8}
              />
            </Tooltip>
          )}
        </div>
      )}

      <div className="gradient-bar__content">
        <div className="gradient-bar__scale">
          {sorted.map((d, i) => {
            const position = (i / sorted.length) * 100;

            return (
              <div
                key={i}
                className="gradient-bar__scale-item"
                style={{ top: `${position}%` }}
              >
                <div className="gradient-bar__scale-value">{d.range[0]}</div>
                <div className="gradient-bar__scale-line" />
              </div>
            );
          })}
        </div>

        <div className="gradient-bar__outer">
          <div className="gradient-bar__inner" style={{ background: gradient }}>
            {markerIndex >= 0 && (
              <div
                className="gradient-bar__inner-marker"
                style={{
                  top: `calc(${markerPosition}% - 8px)`,
                  right: '-50px',
                }}
              >
                <div className="gradient-bar__inner-marker-line" />
                <Typography tag="textsmall_strong">{value}</Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
